import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { getClient } from "../API/clientAPI";
import { getJobOrder } from "../API/jobOrderAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AreasTable from "./FormComponents/AreasTable";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import QcServiceDetailsSection from "./FormComponents/QcServiceDetailsSection";
import QcFindingsRecommendationsSection from "./FormComponents/QcFindingsRecommendationsSection";
import QcCustomerResponsibilitiesSection from "./FormComponents/QcCustomerResponsibilitiesSection";
import QcChemicalControlSection from "./FormComponents/QcChemicalControlSection";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function ViewQcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [error] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage(`Error fetching job order: ${err?.message || err}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]); // Dependency array ensures the effect runs when ID changes

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  // View-only source for Action Taken / Treatment Done
  const act = jobOrder?.action_taken_treatment_done ?? {};

  // --- AREAS TREATED: helpers & constants ---
  const makeIdxKey = (rIdx, cIdx) => `r${rIdx}-c${cIdx}`;

  const AREAS_TREATED_LAYOUT = useMemo(() => [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ], []);

  // find the coordinates of a label (case-insensitive)
  const findLabelKey = useCallback((layout, search) => {
    const target = String(search).toLowerCase();
    for (let r = 0; r < layout.length; r++) {
      for (let c = 0; c < layout[r].length; c++) {
        const lbl = layout[r][c];
        if (lbl && String(lbl).toLowerCase() === target) {
          return makeIdxKey(r, c);
        }
      }
    }
    return null;
  }, []);

  // map selections (1-based in DB) -> state keys, but ONLY for cells that have a non-null label
  const areasPos = useMemo(() => jobOrder?.areas_treated_positions ?? [], [jobOrder?.areas_treated_positions]);
  const areasTreatedViewState = React.useMemo(() => {
    const s = {};
    for (const { row, col } of areasPos) {
      const rIdx = (Number(row) || 1) - 1;
      const cIdx = (Number(col) || 1) - 1;
      if (
        rIdx >= 0 &&
        cIdx >= 0 &&
        AREAS_TREATED_LAYOUT[rIdx] &&
        AREAS_TREATED_LAYOUT[rIdx][cIdx] // ensure label exists
      ) {
        s[makeIdxKey(rIdx, cIdx)] = true;
      }
    }
    return s;
  }, [areasPos, AREAS_TREATED_LAYOUT]);

  // toggle 'Other areas' text enablement only if "Other areas" cell is checked
  const OTHER_AREAS_KEY = React.useMemo(
    () => findLabelKey(AREAS_TREATED_LAYOUT, "Other areas"),
    [AREAS_TREATED_LAYOUT, findLabelKey]
  );

  const otherAreasTextView = jobOrder?.areas_treated_other_text ?? "";

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? '#fff' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  // RHF — single form instance for the page
  const { control, register } = useForm({
    defaultValues: {
      findingsAreas: [],
      recommendations: [],
    },
  });

  const recValues = useWatch({
    control,
    name: "recommendations",
  }) || [];

  // ---- FINDINGS AREAS ----
  const {
    fields: findingsAreas,
    replace: replaceFindingAreas,
  } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  useEffect(() => {
    if (!jobOrder?.findings_areas) return;

    const mapped = jobOrder.findings_areas.map((a) => ({
      location: a?.location ?? "",
      pests: {
        Roaches: Number(a?.pests?.Roaches ?? 0),
        Mosq:    Number(a?.pests?.Mosq    ?? 0),
        Flies:   Number(a?.pests?.Flies   ?? 0),
        Ants:    Number(a?.pests?.Ants    ?? 0),
        Rodents: Number(a?.pests?.Rodents ?? 0),
        Others:  Number(a?.pests?.Others  ?? 0),
      },
    }));

    replaceFindingAreas(mapped);
  }, [jobOrder?.findings_areas, replaceFindingAreas]);

  // ---- RECOMMENDATIONS ----
  const MAX_RECOMMENDATIONS = 8;

  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  const {
    fields: recFields,
    replace: replaceRec,
  } = useFieldArray({
    control,
    name: "recommendations",
  });

  useEffect(() => {
    if (!jobOrder?.recommendations) return;

    const mapped = jobOrder.recommendations.map((r) => ({
      option: r?.option ?? "",
      other_text: r?.other_text ?? r?.otherText ?? "",
    }));

    replaceRec(mapped);
  }, [jobOrder?.recommendations, replaceRec]);

  // View-only source for Customer's Responsibilities
  const cr = jobOrder?.customers_responsibilities ?? {};
  const sanitationList = Array.isArray(cr.sanitation) ? cr.sanitation : [];
  const san1 = sanitationList[0] ?? "";
  const san2 = sanitationList[1] ?? "";
  const san3 = sanitationList[2] ?? "";

  const sealing  = cr.rodent_entry_point?.sealing  ?? "";
  const barriers = cr.rodent_entry_point?.barriers ?? "";

  const repairsMaintenance = cr.repairs_maintenance ?? "";
  const renovation         = cr.renovation ?? "";

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    service_details: "Service Details Completed At",
    areas_treated: "Areas Treated Completed At",
    findings_and_recommendations: "Findings & Recommendations Completed At",
    customer_responsibilities: "Customer Responsibilities Completed At",
    chemical_and_control: "Chemical Used / Control Against Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) => {
    return (
      jobOrder?.[`${stepKey}_completed_at`] ||
      latestStepLogMap?.[stepKey] ||
      ""
    );
  };

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || client || {},
  };

  const natureOfWorkView = {
    generalPestControl: Boolean(jobOrder?.nature_of_work?.general_pest_control),
    disinfection: Boolean(jobOrder?.nature_of_work?.disinfection),
    fumigation: Boolean(jobOrder?.nature_of_work?.fumigation),
    rodentControl: Boolean(jobOrder?.nature_of_work?.rodent_control),
    foggingMisting: Boolean(jobOrder?.nature_of_work?.fogging_misting),
    others: Boolean(jobOrder?.nature_of_work?.others),
  };

  const natureOfWorkOthersTextView =
    jobOrder?.nature_of_work?.others_text || "";

  const structureTypeView = {
    residential: Boolean(jobOrder?.qc_structure_type?.residential),
    commercialEstablishment: Boolean(
      jobOrder?.qc_structure_type?.commercial_establishment
    ),
    others: Boolean(jobOrder?.qc_structure_type?.others),
  };

  const structureNatureOfBusinessView =
    jobOrder?.qc_structure_type?.nature_of_business || "";

  const structureOthersTextView =
    jobOrder?.qc_structure_type?.others_text || "";

  const gpcMainView = {
    insects: Boolean(jobOrder?.gpc?.flying_and_crawling_insects),
    rodents: Boolean(jobOrder?.gpc?.rodents),
    other: Boolean(jobOrder?.gpc?.other_pests),
  };

  const gpcInsectsView = {
    cockroach: Boolean(jobOrder?.gpc?.insects_detail?.cockroach),
    flies: Boolean(jobOrder?.gpc?.insects_detail?.flies),
    mosquitoes: Boolean(jobOrder?.gpc?.insects_detail?.mosquitoes),
  };

  const gpcOtherTextView =
    jobOrder?.gpc?.other_pests_text || "";

  const actionTakenView = {
    mistSpraying: Boolean(jobOrder?.action_taken_treatment_done?.mist_spraying),
    thermalFogging: Boolean(jobOrder?.action_taken_treatment_done?.thermal_fogging),
    coldFogging: Boolean(jobOrder?.action_taken_treatment_done?.cold_fogging),
    larviciding: Boolean(jobOrder?.action_taken_treatment_done?.larviciding),
    gelBaiting: Boolean(jobOrder?.action_taken_treatment_done?.gel_baiting),
    uvLight: Boolean(jobOrder?.action_taken_treatment_done?.uv_light),
    wiping: Boolean(jobOrder?.action_taken_treatment_done?.wiping),
    steaming: Boolean(jobOrder?.action_taken_treatment_done?.steaming),
    others: Boolean(jobOrder?.action_taken_treatment_done?.others),
  };

  const actionTakenOthersTextView =
    jobOrder?.action_taken_treatment_done?.others_text || "";

  const pestTypesView = ["Flies", "Mosquitoes", "Roaches", "Ants", "Rodents"];
  const levelsView = ["L", "M", "H"];

  const infestationFindingsView = {
    Flies: jobOrder?.findings_degree_of_infestation?.flies || "",
    Mosquitoes: jobOrder?.findings_degree_of_infestation?.mosquitoes || "",
    Roaches: jobOrder?.findings_degree_of_infestation?.roaches || "",
    Ants: jobOrder?.findings_degree_of_infestation?.ants || "",
    Rodents: jobOrder?.findings_degree_of_infestation?.rodents || "",
  };

  const customerResponsibilitiesView = {
    sanitation1: san1,
    sanitation2: san2,
    sanitation3: san3,
    sealing,
    barriers,
    repairsMaintenance,
    renovation,
  };

  const chemicalUsedView = (jobOrder?.chemical_used || []).map((item, index) => ({
    label: item?.label || String(index + 1),
    chemical: item?.chemical || "",
  }));

  const controlAgainstView = {
    crawlingInsects: Boolean(jobOrder?.control_against?.crawling_insects),
    flyingInsects: Boolean(jobOrder?.control_against?.flying_insects),
    rodentControl: Boolean(jobOrder?.control_against?.rodent_control),
    others: Boolean(jobOrder?.control_against?.others),
  };

  const otherControlTextView =
    jobOrder?.control_against?.others_text || "";

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: false,
      includeCertificate: true,
      includeReceipt: true,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
    ...(jobOrder?.certificate_url && {
      certificate_url: jobOrder.certificate_url,
    }),
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={client}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={() => {}}
          showStatus={true}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Service Details Section */}
        <Grid item xs={12}>
          <QcServiceDetailsSection
            mode="view"
            natureOfWork={natureOfWorkView}
            natureOfWorkOthersText={natureOfWorkOthersTextView}
            onNatureOfWorkChange={() => () => {}}
            onNatureOfWorkOthersTextChange={() => {}}
            structureType={structureTypeView}
            structureNatureOfBusiness={structureNatureOfBusinessView}
            structureOthersText={structureOthersTextView}
            onStructureTypeChange={() => () => {}}
            onStructureNatureOfBusinessChange={() => {}}
            onStructureOthersTextChange={() => {}}
            gpcMain={gpcMainView}
            gpcInsects={gpcInsectsView}
            gpcOtherText={gpcOtherTextView}
            onGpcMainChange={() => () => {}}
            onGpcInsectChange={() => () => {}}
            onGpcOtherTextChange={() => {}}
            actionTaken={actionTakenView}
            actionTakenOthersText={actionTakenOthersTextView}
            onActionTakenChange={() => () => {}}
            onActionTakenOthersTextChange={() => {}}
            errors={{}}
          />
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.service_details}
              value={getStepTimestamp("service_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* AREAS TREATED Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  AREAS TREATED
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AreasTable
                  layout={AREAS_TREATED_LAYOUT}
                  state={areasTreatedViewState}
                  onToggle={() => () => { /* view-only */ }}
                  ariaLabel="areas_treated"
                  getKey={(rIdx, cIdx /*, label */) => makeIdxKey(rIdx, cIdx)}
                />

                <TextField
                  label="Please specify (Other areas)"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={otherAreasTextView}
                  disabled={!OTHER_AREAS_KEY || !areasTreatedViewState[OTHER_AREAS_KEY]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Areas Treated Completed At"
              value={getStepTimestamp("areas_treated")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Findings and Recommendations Section */}
        <Grid item xs={12}>
          <QcFindingsRecommendationsSection
            mode="view"
            control={control}
            register={register}
            setValue={() => {}}
            clearErrors={() => {}}
            errors={{}}
            pestTypes={pestTypesView}
            levels={levelsView}
            infestationFindings={infestationFindingsView}
            onInfestationChange={() => {}}
            ratingBtnSx={ratingBtnSx}
            findingsAreas={findingsAreas}
            appendFindingArea={() => {}}
            removeFindingArea={() => {}}
            maxFindingsAreas={6}
            recFields={recFields}
            recValues={recValues}
            appendRec={() => {}}
            removeRec={() => {}}
            recommendationOptions={RECOMMENDATION_OPTIONS}
            maxRecommendations={MAX_RECOMMENDATIONS}
          />
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.findings_and_recommendations}
              value={getStepTimestamp("findings_and_recommendations")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* CUSTOMER'S RESPONSIBILITIES Section */}
        <Grid item xs={12}>
          <QcCustomerResponsibilitiesSection
            mode="view"
            value={customerResponsibilitiesView}
            onChange={() => {}}
          />
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.customer_responsibilities}
              value={getStepTimestamp("customer_responsibilities")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Chemical Used and Control Against Section */}
        <Grid item xs={12}>
          <QcChemicalControlSection
            mode="view"
            chemicalUsed={chemicalUsedView}
            onChemicalChange={() => {}}
            controlAgainst={controlAgainstView}
            onControlAgainstChange={() => () => {}}
            otherControlText={otherControlTextView}
            onOtherControlTextChange={() => {}}
            errors={{}}
          />
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.chemical_and_control}
              value={getStepTimestamp("chemical_and_control")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Technician Only Section */}
        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}
      
        {/* Customer Only Section */}
        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {/* Document Links Section */}
        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        {/* Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewQcForm;
